// /utils/apiClient.ts
import type { ApiClient, ApiRequestOptions, ApiError } from "../types/UserTypes";
import { getDevTierOverride } from "./tierContext";
import { getAuthenticatedUserId, getDevUserOverride, getIdentityVersion, isImpersonationRejection, rejectImpersonation } from "./devImpersonation";

function isJsonContentType(value: string): boolean {
  const mediaType = value.split(";")[0].trim().toLowerCase();
  return mediaType === "application/json" || (mediaType.startsWith("application/") && mediaType.endsWith("+json"));
}

export function createApiClient({ baseUrl, token, getToken, tenantId, userId, impersonate = true }: {
  baseUrl: string;
  token?: string | null;
  getToken?: () => Promise<string>;
  tenantId: string | null;
  userId?: number | string | null;
  impersonate?: boolean;
}): ApiClient {
  if (!baseUrl) throw new Error("baseUrl required");

  const normalizeBase = (u: string): string => u.replace(/\/+$/, "");
  const normalizePath = (p: string): string => String(p || "").replace(/^\/+/, "");
  const toUrl = (path: string, query?: ApiRequestOptions["query"]): string => {
    const url = new URL(`${normalizeBase(baseUrl)}/${normalizePath(path)}`);
    if (query && typeof query === "object") {
      Object.entries(query).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, x));
        else url.searchParams.set(k, v);
      });
    }
    return url.toString();
  };

const buildHeaders = (opts: ApiRequestOptions = {}, effectiveToken?: string | null): Record<string, string> => {
  const h: Record<string, string> = { ...(opts.headers || {}) };
  // Identity headers are owned here, including case-insensitive caller overrides.
  Object.keys(h).forEach((key) => {
    if (["x-user-id", "x-impersonate-user-id"].includes(key.toLowerCase())) delete h[key];
  });

  const t = effectiveToken ?? token;
  if (t) h.Authorization = `Bearer ${t}`;

  const tenantRequired = opts.tenantRequired ?? true;

  // ✅ only override if caller actually provided a usable tenantId
  const effectiveTenantId =
    opts.tenantId !== undefined && opts.tenantId !== null && opts.tenantId !== ""
      ? opts.tenantId
      : tenantId;

  if (tenantRequired) {
    if (!effectiveTenantId) {
      throw Object.assign(new Error("Tenant context is missing."), {
        name: "TenantMissingError",
        code: "TENANT_MISSING",
      });
    }
    h["X-Tenant-Id"] = effectiveTenantId;
  }

  // Components may pass the effective user; only the real login identifies the caller.
  const target = impersonate ? getDevUserOverride() : null;
  const callerId = impersonate
    ? getAuthenticatedUserId() ?? (target ? null : userId)
    : userId;
  if (callerId != null) {
    h["X-User-Id"] = String(callerId);
  }
  if (target) h["X-Impersonate-User-Id"] = String(target.userId);

  if (opts.json) h["Content-Type"] = "application/json";
  if (opts.accept) h["Accept"] = opts.accept;

  // Forward dev tier override to backend (dev only, when toggle is on)
  const tierOverride = getDevTierOverride();
  if (tierOverride && localStorage.getItem("devSendTierOverride") !== "false") {
    h["X-Tier-Override"] = tierOverride;
  }

  return h;
};


  const extractErrorMessage = (details: unknown, fallback: string): string => {
    // details can be: string, JSON object, ProblemDetails, ModelState, etc.
    if (typeof details === "string") {
      const s = details.trim();
      if (!s) return fallback;
      // Your backend sometimes returns quoted strings: "User already..."
      return s.replace(/^"|"$/g, "");
    }

    if (details && typeof details === "object") {
      const d = details as Record<string, unknown>;
      if (typeof d.message === "string" && d.message.trim())
        return d.message.trim();
      if (typeof d.error === "string" && d.error.trim())
        return d.error.trim();
      if (typeof d.title === "string" && d.title.trim())
        return d.title.trim();

      // ASP.NET ModelState style: { errors: { Field: ["msg"] } }
      if (d.errors && typeof d.errors === "object") {
        const errors = d.errors as Record<string, unknown>;
        const firstKey = Object.keys(errors)[0];
        const firstVal = errors[firstKey];
        if (Array.isArray(firstVal) && firstVal[0]) return String(firstVal[0]);
      }
    }

    return fallback;
  };

  const parseError = async (res: Response): Promise<ApiError> => {
    const ct = res.headers.get("content-type") || "";
    let details = null;

    try {
      if (isJsonContentType(ct)) details = await res.json();
      else details = await res.text();
    } catch {
      details = null;
    }

    // Build a useful default
    const fallback =
      `${res.status} ${res.statusText || ""}`.trim() ||
      `Request failed (${res.status})`;

    // Detect tier-restricted 403
    const d = details && typeof details === "object" ? details as Record<string, unknown> : null;
    if (res.status === 403 && d?.code === "TIER_RESTRICTED") {
      const message = typeof d.message === "string" && d.message.trim()
        ? d.message
        : `This feature requires the ${d.requiredTier ?? "a higher"} plan.`;
      const err = new Error(message) as ApiError;
      err.name = "ApiError";
      err.status = 403;
      err.details = details;
      err.code = "TIER_RESTRICTED";
      err.feature = typeof d.feature === "string" ? d.feature : undefined;
      err.requiredTier = typeof d.requiredTier === "string" ? d.requiredTier : undefined;
      return err;
    }

    const message = extractErrorMessage(details, fallback);

    const err = new Error(message) as ApiError;
    err.name = "ApiError";
    err.status = res.status;
    err.details = details;
    if (d && typeof d.code === "string") {
      err.code = d.code;
    }
    return err;
  };

  const assertIdentityCurrent = (version: number) => {
    if (version !== getIdentityVersion()) {
      throw new DOMException("User identity changed before response was applied.", "AbortError");
    }
  };

  const send = async (path: string, opts: ApiRequestOptions = {}) => {
    const identityVersion = getIdentityVersion();
    // Get a fresh token before each request (instant cache hit when token is valid)
    const effectiveToken = getToken ? await getToken() : token;
    if (identityVersion !== getIdentityVersion()) {
      throw new DOMException("User identity changed before request dispatch.", "AbortError");
    }
    const url = toUrl(path, opts.query);

    const headers = buildHeaders(opts, effectiveToken);
    const res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body,
      signal: opts.signal,
    });
    assertIdentityCurrent(identityVersion);

    if (!res.ok) {
      const error = await parseError(res);
      assertIdentityCurrent(identityVersion);
      if (headers["X-Impersonate-User-Id"] && isImpersonationRejection(res.status, error.code)) {
        rejectImpersonation(headers["X-Impersonate-User-Id"], identityVersion);
      }
      throw error;
    }
    return { res, identityVersion };
  };

  const request = async (path: string, opts: ApiRequestOptions = {}): Promise<unknown> => {
    const { res, identityVersion } = await send(path, opts);
    assertIdentityCurrent(identityVersion);
    // 204 / empty bodies
    if (res.status === 204) return null;

    const ct = res.headers.get("content-type") || "";
    let result: unknown = null;
    if (isJsonContentType(ct)) result = await res.json();
    // Some APIs return text on success
    else if (ct.includes("text/")) result = await res.text();

    assertIdentityCurrent(identityVersion);
    return result;
  };

  const requestBlob = async (path: string, opts: ApiRequestOptions = {}): Promise<Blob> => {
    const { res, identityVersion } = await send(path, opts);
    assertIdentityCurrent(identityVersion);

    const blob = await res.blob();
    assertIdentityCurrent(identityVersion);
    return blob;
  };

  const guardResponse = (response: Response, version: number): Response => {
    const body = response.body;
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    const guardedBody = body ? new ReadableStream<Uint8Array>({
      async pull(controller) {
        assertIdentityCurrent(version);
        reader ??= body.getReader();
        const chunk = await reader.read();
        assertIdentityCurrent(version);
        if (chunk.done) {
          reader.releaseLock();
          controller.close();
        } else controller.enqueue(chunk.value);
      },
      cancel: (reason: unknown) => reader ? reader.cancel(reason) : body.cancel(reason),
    }, { highWaterMark: 0 }) : null;
    return new Proxy(response, {
      get(target, property) {
        if (property === "body") {
          assertIdentityCurrent(version);
          return guardedBody;
        }
        if (property === "clone") return () => {
          assertIdentityCurrent(version);
          return guardResponse(target.clone(), version);
        };
        const value: unknown = Reflect.get(target, property, target);
        if (typeof value !== "function") return value;
        if (["arrayBuffer", "blob", "bytes", "formData", "json", "text"].includes(String(property))) {
          return async (...args: unknown[]) => {
            assertIdentityCurrent(version);
            const result: unknown = await Reflect.apply(value, target, args);
            assertIdentityCurrent(version);
            return result;
          };
        }
        return value.bind(target);
      },
    });
  };

  return {
    get: (p, opts) => request(p, { ...opts, method: "GET" }),
    getBlob: (p, opts) => requestBlob(p, { ...opts, method: "GET" }),
    postResponse: async (p, body, opts = {}) => {
      const { res, identityVersion } = await send(p, {
        ...opts, method: "POST", json: true,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      assertIdentityCurrent(identityVersion);
      return guardResponse(res, identityVersion);
    },
    post: (p, body, opts) =>
      request(p, {
        ...opts,
        method: "POST",
        body: body === undefined ? undefined : JSON.stringify(body),
        json: true,
      }),
    put: (p, body, opts) =>
      request(p, {
        ...opts,
        method: "PUT",
        body: body === undefined ? undefined : JSON.stringify(body),
        json: true,
      }),
    delete: (p, opts) => request(p, { ...opts, method: "DELETE" }),

    // file uploads
    postForm: (p, formData, opts) =>
      request(p, { ...opts, method: "POST", body: formData }),
  } as ApiClient;
}
